import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaUsers } from "react-icons/fa";
import { motion } from "framer-motion";
import {
  useCreateCompanyVerificationMutation,
  useDeleteCompanyVerificationMutation,
  useGetCompanyVerificationsQuery,
} from "reduxFiles/api/companyApi";
import { CompanyVerification } from "types/companyTypes";
import HeaderBox from "components/navbar/HeaderBox";
import DynamicTable from "components/table/DynamicTable";
import Pagination from "components/table/Pagination";
import ConfirmModal from "components/modal/ConfirmModal";
import { useModal } from "hooks/useModal";
import moment from "moment-jalaali";
import { Column, TableAction } from "types/globalTypes";
import TextArea from "components/input/TextArea";
import { Field } from "components/dynamic/DynamicForm";
import MobileFilterDrawer from "components/filter/MobileFilterDrawer";
import DynamicFilter from "components/filter/DynamicFilter";
import { FieldValues } from "react-hook-form";

const CompanyVerifications: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // states
  const [newData, setNewData] = useState<string>("");
  const [item, setItem] = useState<CompanyVerification | null>(null);
  const confirmModal = useModal();
  const modal = useModal();

  // filters
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 30,
  });

  // apis
  const { data, isFetching, refetch } = useGetCompanyVerificationsQuery({
    page: pagination.page,
    limit: pagination.limit,
    filters,
  });

  const [createVerification, { isLoading: isCreating }] =
    useCreateCompanyVerificationMutation();
  const [deleteVerification, { isLoading: isDeleting }] =
    useDeleteCompanyVerificationMutation();

  const getStatusColor = (hasCompany: boolean) => {
    return hasCompany
      ? "bg-green-500/10 text-green-600 dark:bg-green-500/20 dark:text-green-400"
      : "bg-amber-500/10 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400";
  };

  const getStatusText = (hasCompany: boolean) => {
    return hasCompany ? t("company.created") : t("company.notCreated");
  };

  // filters
  const fields: Field[] = [
    {
      name: "nationalId",
      label: t("company.fields.nationalId"),
      type: "text",
      filtered: true,
    },
    {
      name: "verified",
      label: t("company.fields.verified"),
      type: "select",
      options: [
        {
          label: t("company.fields.true"),
          value: "true",
        },
        {
          label: t("company.fields.false"),
          value: "false",
        },
      ],
      filtered: true,
    },
  ];

  // columns
  const columns: Column<CompanyVerification>[] = [
    {
      key: "createdAt",
      title: t("table.createdAt"),
      render: (row) =>
        moment(row.createdAt).format(
          lang === "fa" ? "jYYYY/jMM/jDD HH:mm" : "YYYY/MM/DD HH:mm"
        ),
    },
    {
      key: "nationalId",
      title: t("companyVerification.nationalId"),
    },
    {
      key: "company",
      title: t("companyVerification.status"),
      render: (row) => (
        <div className="flex flex-col items-center justify-center">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${getStatusColor(
              !!row.company
            )}`}
          >
            <span
              className={`h-2 w-2 rounded-full ${
                !!row.company ? "bg-green-500" : "bg-amber-500"
              }`}
            />
            {getStatusText(!!row.company)}
          </span>
        </div>
      ),
    },
    {
      key: "company",
      title: t("companyVerification.company"),
      render: (row) => row.company?.name ?? "-",
    },
  ];

  // actions
  const actions: TableAction<CompanyVerification>[] = [
    {
      label: t("companyVerification.delete"),
      delete: true,
      onClick: (row) => {
        setItem(row);
        confirmModal.openModal();
      },
      color: "error",
    },
  ];

  // handlers
  const handleFilterSubmit = (filterData: FieldValues) => {
    setFilters(filterData);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination({
      page: 1,
      limit: newLimit,
    });
  };

  const handleDelete = async () => {
    try {
      if (!item) return;
      await deleteVerification(item.id).unwrap();
      confirmModal.closeModal();
      setTimeout(() => {
        setItem(null);
        refetch();
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  const handleBulkCreate = async (nationalIds: string[]) => {
    try {
      await createVerification({ nationalIds: nationalIds }).unwrap();
      modal.closeModal();
      setNewData("");
      refetch();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="mt-5 h-full">
        <HeaderBox
          title={t("companyVerification.title")}
          createBtn={{
            title: t("companyVerification.bulkAdd"),
            handler: modal.openModal,
          }}
          filterBtn={{
            title: t("buttons.filters"),
            handler: () => setShowFilters((prev) => !prev),
          }}
          refreshBtn={refetch}
        />

        <MobileFilterDrawer
          open={showFilters}
          onClose={() => setShowFilters((prev) => !prev)}
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          loading={isFetching}
        />
        <DynamicFilter
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          defaultValues={{}}
          loading={isFetching}
        />

        <DynamicTable<CompanyVerification>
          columns={columns}
          data={data?.items ?? []}
          actions={actions}
          loading={isFetching}
          pagination={pagination}
        />

        <Pagination
          currentPage={pagination?.page || 1}
          totalPages={data?.pageCount || 1}
          onPageChange={(newPage) => {
            setPagination((prev) => ({ ...prev, page: newPage }));
          }}
          totalItems={data?.total || 0}
          currentLimit={pagination.limit}
          onLimitChange={handleLimitChange}
        />

        {/* Bulk Create Modal */}
        {modal.isOpen && (
          <div className="bg-black/50 dark:bg-black/70 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-md">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-800"
            >
              <div className="p-6">
                <div className="mb-6 flex items-center gap-3">
                  <div className="rounded-xl bg-purple-500/10 p-2 dark:bg-purple-500/20">
                    <FaUsers className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      {t("companyVerification.bulkAdd")}
                    </h3>
                    <p className="text-sm text-gray-700 dark:text-gray-400">
                      {t("companyVerification.bulkAddDescription")}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t("companyVerification.nationalIds")}
                    </label>
                    <TextArea
                      value={newData}
                      onChange={(value) => setNewData(value)}
                      className="focus:border-transparent w-full rounded-xl border border-gray-300 bg-white px-4 py-3 font-mono text-gray-900 transition-all duration-300 focus:ring-2 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-purple-400"
                      rows={8}
                      placeholder={t(
                        "companyVerification.nationalIdsPlaceholder"
                      )}
                    />
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-3">
                  <button
                    onClick={() => {
                      modal.closeModal();
                      setItem(null);
                    }}
                    className="flex-1 rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors duration-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    {t("buttons.cancel")}
                  </button>
                  <button
                    onClick={() => {
                      const ids = newData
                        .split("\n")
                        .map((id) => id.trim())
                        .filter((id) => id.length > 0);

                      if (ids.length === 0) {
                        return;
                      }

                      handleBulkCreate(ids);
                    }}
                    disabled={isCreating}
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-medium text-white shadow-lg shadow-brand-500/25 transition-colors duration-300 hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-500 dark:shadow-brand-500/20 dark:hover:bg-brand-600"
                  >
                    {isCreating ? (
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    ) : (
                      <FaUsers className="h-4 w-4" />
                    )}
                    {t("buttons.add")}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => {
            setItem(null);
            confirmModal.closeModal();
          }}
          handler={handleDelete}
          loading={isDeleting}
          title={t("companyVerification.delete")}
          desc={t("actions.confirmDeleteDesc", {
            name: item?.nationalId || "",
          })}
        />
      </div>
    </div>
  );
};

export default CompanyVerifications;
